'use client'

import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion'
import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useCurrentCurrency } from '@repo/cf-user/hooks/use-current-currency'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { CurrencyCode } from '@repo/graphql-types'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { useFeatureValue } from 'growthbook'
import { useCountdownToMidnight } from 'cf-banners/components/all-access-yearly-trial'

interface CountdownLabels {
  tryFreeToday: string
  hours: string
  mins: string
  secs: string
}

function BannerCountdown({ labels }: { labels: CountdownLabels }) {
  const { hours, minutes, seconds, dateTime } = useCountdownToMidnight()

  return (
    <div className="flex items-center gap-1">
      <span className="text-12 text-cf-grey-800">{labels.tryFreeToday}</span>
      <time
        className="text-14 font-semibold text-cf-red-800 flex gap-0.5 tabular-nums"
        dateTime={dateTime}
        aria-live="off"
      >
        <span>{String(hours).padStart(2, '0')}</span>
        <span>:</span>
        <span>{String(minutes).padStart(2, '0')}</span>
        <span>:</span>
        <span>{String(seconds).padStart(2, '0')}</span>
      </time>
    </div>
  )
}

export function FreeTrialBanner({
  title,
  subTitle,
  cta,
  cancelText,
  countdownLabels,
}: {
  title: string
  subTitle: string
  cta: string
  cancelText: string
  countdownLabels: CountdownLabels
}) {
  const countdownVariant = useFeatureValue('GROW-2291-add-countdown', 0)
  const showCountdown = countdownVariant === 1

  const currency = useCurrentCurrency()
  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.code || CurrencyCode.Usd,
  }).format(0)
  const ctaText = `${cta} (${zeroPrice})`

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        layout
        initial={false}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={tw(
          'fixed-banner',
          'text-service-white flex w-full flex-col items-center justify-center gap-[10px] border p-[10px] shadow-md md:flex-row',
          'bg-cf-mint border-green-500/10',
          'relative',
          'overflow-hidden',
        )}
      >
        <div
          className={tw(
            'flex w-full flex-col items-center justify-between md:flex-row',
            'max-w-[1200px]',
          )}
        >
          <AnimatePresence mode="wait">
            <m.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={tw('flex w-full flex-col items-center justify-between md:flex-row')}
            >
              <p className={tw('text-16 text-aubergine-500 ml-[10px]')}>
                <span className={tw('block font-bold leading-[22px] ', 'text-18 md:text-24')}>
                  {title}
                </span>
                {subTitle ? (
                  <span className={tw('text-14 mb-2 block font-semibold md:mb-0')}>{subTitle}</span>
                ) : null}
              </p>
              <div className="flex items-center gap-1 flex-col">
                {showCountdown ? <BannerCountdown labels={countdownLabels} /> : null}

                <Button cta={ctaText} />
                <div className="text-xs font-bold text-aubergine-500">{cancelText}</div>
              </div>
            </m.div>
          </AnimatePresence>
        </div>
      </m.div>
    </LazyMotion>
  )
}

function Button({ cta }: { cta: string }) {
  return (
    <StartFreeTrialButton
      className={tw(
        'text-14 flex h-[30px] w-full cursor-pointer items-center justify-center rounded px-5 text-center font-bold uppercase md:h-[40px] md:w-fit whitespace-nowrap',
        'bg-secondary-500 hover:bg-secondary-700',
      )}
      label={cta}
      entryPoint={
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith(AppRoutes.COLORING_PAGES)
          ? PAYMENT_ENTRY_POINTS.FREEBIES_BANNER
          : PAYMENT_ENTRY_POINTS.FLOATING_BANNER
      }
      onClick={() => {
        sendAmplitudeEvent(AmplitudeEvents.FREE_TRIAL_BUTTON_CLICKED)
      }}
    />
  )
}
